import { Injectable } from "@angular/core";
import { Adapter } from "@core/adapter";

/**
 * A certification in an organisation's catalogue, together with its supersession edges.
 *
 * - `covers`     — node IRIs this certification also satisfies ("grants"; outgoing edges).
 * - `coveredBy`  — node IRIs that satisfy this certification ("granted by"; incoming edges).
 *
 * Both directions come down in the collection payload as IRI arrays, so the graph is never inverted
 * client-side. Edges point at node IRIs (@id), not certification IRIs. A certification appears at
 * most once per organisation, so certification.id is safe to key on.
 */
export interface CertificationNode {
  id: string;
  // @id, the value the edge arrays point at.
  iri: string;
  certification: {
    id: string;
    name: string;
    defaultValidity: number | null;
    defaultValidityUnit: string | null;
  };
  covers: string[];
  coveredBy: string[];
}

@Injectable({
  providedIn: 'root'
})
export class CertificationNodeAdapter implements Adapter<CertificationNode> {
  adapt(item: any): CertificationNode {
    return {
      id: item.id,
      // Prefer the @id the API sends; fall back to deriving it so edge matching never breaks.
      iri: item['@id'] ?? `/organisation_profile_certification_nodes/${item.id}`,
      certification: {
        id: item.certification?.id ?? '',
        name: item.certification?.name ?? '',
        defaultValidity: item.certification?.defaultValidity ?? null,
        defaultValidityUnit: item.certification?.defaultValidityUnit ?? null,
      },
      covers: Array.isArray(item.covers) ? item.covers.map(String) : [],
      coveredBy: Array.isArray(item.coveredBy) ? item.coveredBy.map(String) : [],
    };
  }
}

/** Index the graph by node IRI — the key both edge arrays reference. */
export function indexByIri(nodes: CertificationNode[]): Map<string, CertificationNode> {
  const byIri = new Map<string, CertificationNode>();
  nodes.forEach(node => byIri.set(node.iri, node));
  return byIri;
}

/**
 * Every certification reachable by following `covers` from a node — direct edges plus everything
 * inherited through them, excluding the node itself. This is the same walk the backend uses, so the
 * preview always agrees with server-side resolution.
 *
 * The `seen` guard is required, not defensive: diamonds (A covers A.1 and A.2, both cover Base)
 * reach Base twice.
 */
export function reachable(nodeIri: string, byIri: Map<string, CertificationNode>): Set<string> {
  const seen = new Set<string>();
  const stack = [...(byIri.get(nodeIri)?.covers ?? [])];
  while (stack.length) {
    const iri = stack.pop()!;
    if (iri === nodeIri || seen.has(iri) || !byIri.has(iri)) continue;
    seen.add(iri);
    stack.push(...byIri.get(iri)!.covers);
  }
  return seen;
}

/**
 * For each *inherited* certification (reachable via `covers` but not a direct edge), the direct edge
 * it comes through — for the "inherited via X" label. Direct edges themselves are excluded; the
 * first direct edge that reaches a target wins.
 */
export function inheritedVia(nodeIri: string, byIri: Map<string, CertificationNode>): Map<string, string> {
  const direct = new Set(byIri.get(nodeIri)?.covers ?? []);
  const via = new Map<string, string>();
  direct.forEach(edge => {
    const through = reachable(edge, byIri);
    through.add(edge);
    through.forEach(target => {
      if (target === nodeIri || direct.has(target) || via.has(target)) return;
      via.set(target, edge);
    });
  });
  return via;
}
